export interface GsapPluginConfig {
	name: string;
	displayName: string;
	description: string;
	fileSize: number; // in KB
	cdnUrl: string;
	dependencies: string[];
	category: 'interaction' | 'text' | 'svg' | 'motion' | 'integration' | 'effects';
}

export interface GsapPluginSelection {
	name: string;
	enabled: boolean;
	version?: string;
	fileSize: number;
	cdnUrl?: string;
}

export const GSAP_PLUGINS: GsapPluginConfig[] = [
	{
		name: 'TextPlugin',
		displayName: 'Text Plugin',
		description: 'Animate text content character by character',
		fileSize: 8,
		cdnUrl: 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.13.0/TextPlugin.min.js',
		dependencies: [],
		category: 'text'
	},
	{
		name: 'DrawSVGPlugin',
		displayName: 'DrawSVG Plugin',
		description: 'Animate SVG stroke paths for drawing effects',
		fileSize: 12,
		cdnUrl: 'https://cdn.jsdelivr.net/npm/gsap@3.13.0/dist/DrawSVGPlugin.min.js',
		dependencies: [],
		category: 'svg'
	},
	{
		name: 'MorphSVGPlugin',
		displayName: 'MorphSVG Plugin',
		description: 'Morph between different SVG shapes smoothly',
		fileSize: 18,
		cdnUrl: 'https://cdn.jsdelivr.net/npm/gsap@3.13.0/dist/MorphSVGPlugin.min.js',
		dependencies: [],
		category: 'svg'
	},
	{
		name: 'MotionPathPlugin',
		displayName: 'MotionPath Plugin',
		description: 'Animate elements along custom SVG paths',
		fileSize: 15,
		cdnUrl: 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.13.0/MotionPathPlugin.min.js',
		dependencies: [],
		category: 'motion'
	},
	{
		name: 'SplitText',
		displayName: 'SplitText Plugin',
		description: 'Split text into lines, words, and characters for complex text animations',
		fileSize: 20,
		cdnUrl: 'https://cdn.jsdelivr.net/npm/gsap@3.13.0/dist/SplitText.min.js',
		dependencies: [],
		category: 'text'
	},
	{
		name: 'CustomEase',
		displayName: 'Custom Ease',
		description: 'Create custom easing curves for unique animation timing',
		fileSize: 6,
		cdnUrl: 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.13.0/CustomEase.min.js',
		dependencies: [],
		category: 'effects'
	},
	{
		name: 'Flip',
		displayName: 'Flip Plugin',
		description: 'Animate layout changes smoothly with FLIP technique',
		fileSize: 25,
		cdnUrl: 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.13.0/Flip.min.js',
		dependencies: [],
		category: 'effects'
	}
];

export function getPluginByName(name: string): GsapPluginConfig | undefined {
	return GSAP_PLUGINS.find(plugin => plugin.name === name);
}

export function calculateTotalFileSize(selections: GsapPluginSelection[]): number {
	return selections.reduce((total, selection) => {
		if (selection.enabled) {
			return total + selection.fileSize;
		}
		return total;
	}, 0);
}

export function getPluginDependencies(pluginName: string): string[] {
	const plugin = getPluginByName(pluginName);
	return plugin?.dependencies || [];
}

export function validatePluginSelections(selections: GsapPluginSelection[]): {
	valid: boolean;
	missingDependencies: string[];
} {
	const selectedNames = new Set(selections.filter(s => s.enabled).map(s => s.name));
	const missingDependencies: string[] = [];

	selections.forEach(selection => {
		if (selection.enabled) {
			const dependencies = getPluginDependencies(selection.name);
			dependencies.forEach(dep => {
				if (!selectedNames.has(dep)) {
					missingDependencies.push(`${selection.name} requires ${dep}`);
				}
			});
		}
	});

	return {
		valid: missingDependencies.length === 0,
		missingDependencies
	};
}
